/**
 * triggerCoinWait — the Hold & Win TRIGGER coins wait, they do not win
 * (Vladimir, 2026-10-07: "when we get 6 of them and trigger the feature, let's
 * not play win at that time, let's just play this wait animation on all of
 * them … we save that win for later, when the scarab hits it").
 *
 * The trigger line (useWinController / useFreeSpinBasicWinController
 * cycleThroughLines) used to loop each coin's `win` until the intro took the
 * screen. Now every trigger coin plays its anticipation wait — wait_in ->
 * wait_loop — the clip the coins already down played during the
 * anticipation:
 *
 *   a coin already waiting   (the anticipation started it) is left alone —
 *                            syncWinAnimation would restart it from setup;
 *   any other coin           goes through syncWinAnimation as before (it
 *                            brings the spine up over its picture and writes
 *                            its value), then straight onto its wait clips;
 *   a coin without them      keeps the `win` that call started.
 */

import { COIN_JACKPOT_SYMBOL_ID } from "../../SymbolData";
import { wearJackpotCoinSkin } from "./jackpotCoinSkin";

const WAIT_IN = "wait_in";
const WAIT_LOOP = "wait_loop";
// Blend from what the spine shows into its wait, seconds (tensionSymbols'
// WAIT_MIX).
const WAIT_MIX = 0.2;

const isWaiting = (spine) =>
  /^wait/.test(spine?.state?.tracks?.[0]?.animation?.name || "");

/**
 * @param {object} overlayCtrl   The reel's OverlayReelController.
 * @param {number} row           The cell.
 * @param {number} symbolId      The coin's id.
 * @param {string} animType      What the line would have played (`win`).
 * @param {number|null} [betOverride]
 * @param {object|null} [round]  The board's round — a jackpot coin wears its
 *                               tier's skin from it. On a reload the coin
 *                               reaches its wait without ever landing, so
 *                               nothing else has put the skin on.
 */
export const playTriggerCoinWait = (
  overlayCtrl,
  row,
  symbolId,
  animType,
  betOverride = null,
  round = null,
) => {
  if (!overlayCtrl) return;
  if (symbolId === COIN_JACKPOT_SYMBOL_ID) {
    wearJackpotCoinSkin(overlayCtrl, round, overlayCtrl.reelIndex, row);
  }
  const spineFor = () => overlayCtrl.prePositionedSpines?.get?.(`${row}_${symbolId}`);
  const already = spineFor();
  if (already?.visible && isWaiting(already)) return;

  overlayCtrl.syncWinAnimation(row, symbolId, animType, betOverride, 0);

  const spine = spineFor();
  const data = spine?.skeleton?.data;
  const state = spine?.state;
  if (!state || !data?.findAnimation(WAIT_LOOP)) return; // no wait clips: `win` stays
  if (data.findAnimation(WAIT_IN)) {
    const first = state.setAnimation(0, WAIT_IN, false);
    if (first) first.mixDuration = WAIT_MIX;
    state.addAnimation(0, WAIT_LOOP, true, 0);
  } else {
    const loop = state.setAnimation(0, WAIT_LOOP, true);
    if (loop) loop.mixDuration = WAIT_MIX;
  }
  spine.autoUpdate = true;
};
